import React, { lazy } from "react";
import { RouteObject, Navigate } from "react-router";

// Layouts
import { Layout } from "./appLayout";
import { LayoutWithHeaderAndSidebar } from "@/shared/widgets/Layout/LayoutWithHeaderAndSidebar";
import ErrorPage from "@/shared/widgets/ErrorBoundary/ErrorPage";

// Route Guards
import PrivateRoute from "@/shared/utils/router/PrivateRoute";
import LandingRoute from "@/shared/utils/router/LandingRoute";

// Sub-routes
import { workspaceRoutes } from "./workspaceRoutes";
import { profileRoutes } from "./profileRoutes";
import { organisationRoutes } from "./organisiationRoutes";
import { shareRoutes } from "./shareRoutes";

// Lazy load auth pages (accessed infrequently after initial login)
const Login = lazy(() => import("@/features/auth/pages/login"));
const SignupPage = lazy(() => import("@/features/auth/pages/signup"));
const InvitePage = lazy(() => import("@/features/auth/pages/invite"));
const ResetPassword = lazy(
  () => import("@/features/auth/pages/password-reset")
);
const AuthCallback = lazy(() => import("@/features/auth/pages/auth-callback"));
const PersonaSelect = lazy(
  () => import("@/features/auth/pages/persona-select")
);

// Lazy load surveys pages (accessed less frequently)
const Surveys = lazy(() => import("@/features/surveys/pages/SurveysListPage"));
const SurveyIntro = lazy(
  () => import("@/features/surveys/pages/survey/SurveyPage")
);

// Lazy load assessment order pages
const AssessmentOrderPage = lazy(
  () => import("@/features/assessment-orders/pages/AssessmentOrderPage")
);
const AssessmentOrderCompletionPage = lazy(
  () =>
    import("@/features/assessment-orders/pages/AssessmentOrderCompletionPage")
);

// Lazy load other infrequent pages
const OrganisationSelect = lazy(
  () => import("@/features/organization/pages/OrganizationSelectPage")
);
const NewPassword = lazy(
  () => import("@/features/profile/pages/NewPasswordPage")
);

/**
 * Main application routes using Route Objects pattern
 * More type-safe and maintainable than JSX routes
 *
 * @see https://reactrouter.com/home
 */
export const routes: RouteObject[] = [
  {
    element: <Layout />,

    children: [
      // Public Auth Routes
      {
        element: <LandingRoute />,
        children: [
          {
            path: "/",
            Component: Login,
          },
          {
            path: "/login",
            Component: Login,
          },
          {
            path: "/signup",
            Component: SignupPage,
          },
          {
            path: "/invite/:inviteSecret",
            Component: InvitePage,
          },
          {
            path: "/reset-password/:resetSecret",
            Component: ResetPassword,
          },
        ],
      },

      // Auth Callback
      {
        path: "/auth/success",
        Component: AuthCallback,
      },

      // Public Share Routes
      ...shareRoutes,

      // Private Routes
      {
        element: <PrivateRoute />,
        children: [
          // Main App Routes with Sidebar
          {
            element: <LayoutWithHeaderAndSidebar />,
            children: [
              ...workspaceRoutes,
              ...profileRoutes,
              ...organisationRoutes,
            ],
          },

          // Other Private Routes (without sidebar)
          {
            path: "/onboarding",
            Component: PersonaSelect,
          },
          {
            path: "/newPassword",
            Component: NewPassword,
          },
          {
            path: "/surveys",
            Component: Surveys,
          },
          {
            path: "/survey/:surveyId",
            Component: SurveyIntro,
          },
          {
            path: "/assessment-order/:id",
            Component: AssessmentOrderPage,
          },
          {
            path: "/assessment-order/:id/completion",
            Component: AssessmentOrderCompletionPage,
          },
          {
            path: "/org",
            Component: OrganisationSelect,
          },
        ],
      },

      // 404 Fallback
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
];
